import {systemLang} from '../../../../../Localization/languages';
import {getLang, isRTL} from '../../../../../Localization';
import {AlertValidForCategory} from '../../../enums';
import {texts as commonAutomationTexts} from '../../../texts';
import {texts as bpmTexts} from '../texts';
import {RecipientsType} from '../../../components/RuleDialog/parts/AutomationActionTemplate/BusinessRuleActionTemplate/enum';
import {BusinessRuleAction, businessRulesActionMap} from '../../../constants';
import {AutomationChoose} from '../../../reducer/model';
import {AlertOverviewType} from '../api/types';
import {priority} from '@priority-software/priority-proto-api/lib';
import {bpmRulesStorage} from '../BPM.rules.storage';

type IAlertChooseGWT = {
    id?: string;
    title?: string;
    desc?: string;
    zoom?: string;
    name?: string;
    type?: string;
    readonly?: '0' | '1';
};
type IAlertTemplateChooseGWT = IAlertChooseGWT & {
    selected?: string;
    language?: string;
};

type IAlertChooseDocumentsGWT = {
    id?: number;
    title?: string;
    desc?: string;
    zoom?: string;
    readonly?: '0' | '1';
    PrintFormats?: IAlertChooseGWT[] | null;
    PrintTemplates?: IAlertTemplateChooseGWT[] | null;
    PrintTemplatesForeignLang?: IAlertTemplateChooseGWT[] | null;
};

type GWTAutomationChooses = {
    TimeUnits?: IAlertChooseGWT[] | null;
    Documents?: IAlertChooseDocumentsGWT[] | null;
    Users?: IAlertChooseGWT[] | null;
    ExprPredefined?: IAlertChooseGWT[] | null;
    ExprFunc?: IAlertChooseGWT[] | null;
    Groups?: IAlertChooseGWT[] | null;
    MessageTypes?: IAlertChooseGWT[] | null;
    MailFields?: IAlertChooseGWT[] | null;
    MailUserFields?: IAlertChooseGWT[] | null;
    MailTypes?: IAlertChooseGWT[] | null;
    Ops?: IAlertChooseGWT[] | null;
    CurValues?: IAlertChooseGWT[] | null;
    Fields?: IAlertChooseGWT[] | null;
    EngFields?: IAlertChooseGWT[] | null;
    ValidTypes?: IAlertChooseGWT[] | null;
    PrivGroups?: IAlertChooseGWT[] | null;
    ValueFields?: IAlertChooseGWT[] | null;
    DateFields?: IAlertChooseGWT[] | null;
    Webhooks?: IAlertChooseGWT[] | null;
};

function capitalizeFirstLetter(string: string = '') {
    return string.charAt(0).toUpperCase() + string.toLowerCase().slice(1);
}

const defaultChooseMapper = (data: IAlertChooseGWT[]) => {
    return data.map((item: IAlertChooseGWT) => {
        const additionalData =
            item.type && item.name ? {Name: item.name, Type: capitalizeFirstLetter(item.type)} : {};
        return {
            value: Number(item.id),
            label: item.title,
            description: item.desc,
            data: {
                Id: item.id,
                Title: item.title,
                Zoom: item.zoom,
                ...additionalData
            }
        } as AutomationChoose;
    });
};

const bpmRulesActionTexMap = {
    [BusinessRuleAction.PushNotification]: bpmTexts.push_notification_action,
    [BusinessRuleAction.AllowStatusChange]: bpmTexts.allow_status_change_action,
    [BusinessRuleAction.AssignTo]: bpmTexts.assign_to_action
};

const messageTypesChooseMapper = (data: IAlertChooseGWT[], isPath?: boolean) => {
    return data
        .filter(item => {
            const conditionField = isPath
                ? BusinessRuleAction.ChangeStatusTo
                : BusinessRuleAction.AllowStatusChange;

            return (
                item.id !== BusinessRuleAction.UserVerification.toString() &&
                item.id !== conditionField.toString()
            );
        })
        .map((item: IAlertChooseGWT) => {
            const customData = businessRulesActionMap[Number(item.id)];
            const BPMCustomLabel = bpmRulesActionTexMap[item.id];
            const tooltip =
                Number(item.id) === BusinessRuleAction.PushNotification
                    ? bpmTexts.push_notification_action_tooltip
                    : undefined;

            const getLabel = () => {
                if (BPMCustomLabel) {
                    return BPMCustomLabel;
                }
                if (customData?.textField) {
                    return commonAutomationTexts[customData?.textField];
                }
                return item.title;
            };

            return {
                value: Number(item.id),
                label: getLabel(),
                icon: customData?.icon,
                description: item.desc,
                data: {
                    id: item.title,
                    tooltip
                }
            } as AutomationChoose;
        });
};

const engFieldsChooseMapper = (engFields: IAlertChooseGWT[], fields: IAlertChooseGWT[]) => {
    return engFields.map((engField: IAlertChooseGWT) => {
        const heFields = isRTL()
            ? fields?.find(field => Number(field.id) === Number(engField.id)) || {}
            : {};

        return {
            value: engField.id,
            label: isRTL() ? heFields.title : engField.title,
            description: engField.title,
            data: {
                Id: engField?.id,
                Title: engField?.title
            }
        } as AutomationChoose;
    });
};

const mailTypesChooseMapper = (data: IAlertChooseGWT[]) => {
    return data.map((item: IAlertChooseGWT) => {
        const data = {
            value: Number(item.id),
            label: item.title,
            data: item
        };
        if (data.value === RecipientsType.email) {
            data.label = commonAutomationTexts.email_field_label;
        }
        return data;
    });
};

const documentsChooseMapper = (data: IAlertChooseDocumentsGWT[], systemLangs: systemLang[]) => {
    return data.map((item: IAlertChooseDocumentsGWT) => {
        const langsDictionary = systemLangs.reduce((prev, curr) => {
            return {...prev, [curr.lang]: curr.langtitle};
        }, {});
        return {
            label: item.title,
            value: Number(item.id),
            data: {
                printFormats:
                    item.PrintFormats?.map(format => {
                        return {
                            label: format.title,
                            value: Number(format.id)
                        };
                    }) || [],
                printTemplates:
                    [
                        ...(item.PrintTemplates || []),
                        ...(item.PrintTemplatesForeignLang || [])
                    ]?.map(format => {
                        const formatLanguageNumber = format.language || getLang();
                        const formatLanguage = langsDictionary[formatLanguageNumber];

                        return {
                            label: `${format.title} (${formatLanguage})`,
                            value: Number(format.id)
                        };
                    }) || []
            }
        };
    });
};

const validTypesChooseMapper = (data: IAlertChooseGWT[]) => {
    return data.map((item: IAlertChooseGWT) => {
        const id = Number(item.id);
        const data = {
            value: id,
            label: item.title,
            data: item
        };
        if (id === AlertValidForCategory.userInGroup) {
            data.label = commonAutomationTexts.my_privilege_group_label;
        } else if (id === AlertValidForCategory.group) {
            data.label = commonAutomationTexts.spec_privilege_group_label;
        } else if (id === AlertValidForCategory.user) {
            data.label = commonAutomationTexts.specific_user_option_text;
        }
        return data;
    });
};

export const prepareChooses = (
    data: GWTAutomationChooses,
    systemLangs: systemLang[],
    isPath?: boolean
) => {
    return {
        TimeUnits: defaultChooseMapper(data.TimeUnits || []),
        Documents: documentsChooseMapper(data.Documents || [], systemLangs),
        User: defaultChooseMapper(data.Users || []),
        ExprPredefined: defaultChooseMapper(data.ExprPredefined || []),
        ExprFunc: defaultChooseMapper(data.ExprFunc || []),
        Groups: defaultChooseMapper(data.Groups || []),
        MessageTypes: messageTypesChooseMapper(data.MessageTypes || [], isPath),
        MailFields: defaultChooseMapper(data.MailFields || []),
        MailUserFields: defaultChooseMapper(data.MailUserFields || []),
        MailTypes: mailTypesChooseMapper(data.MailTypes || []),
        Ops: defaultChooseMapper(data.Ops || []),
        CurrrentValues: defaultChooseMapper(data.CurValues || []),
        Fields: defaultChooseMapper(data.Fields || []),
        EnglishFields: engFieldsChooseMapper(data.EngFields || [], data.Fields || []),
        ValidTypes: validTypesChooseMapper(data.ValidTypes || []),
        PrivilegeGroups: defaultChooseMapper(data.PrivGroups || []),
        ValueFields: defaultChooseMapper(data.ValueFields || []),
        DateFields: defaultChooseMapper(data.DateFields || []),
        Webhooks: defaultChooseMapper(data.Webhooks || [])
    };
};

export const generateAlertList = (data: AlertOverviewType[], actionChooses: any[] = []) => {
    return data.map((rule: AlertOverviewType) => {
        return new priority.netitems.form.Alert({
            AlertNumber: rule.alert ? Number(rule.alert) : 0,
            Description: rule.description,
            Active: rule.isActive === 'Y',
            BPM: {
                TimeValue: null,
                TimeUnit: null,
                TimeField: null,
                ToStatus: null
            },
            MessageType: actionChooses.find(item => item.data?.id === rule.action)?.value || null,
            LastMobifyBy: rule.user,
            ConditionsOperator: rule.conditionsOperator === '1',
            AlertConds:
                rule.Conditions?.map(cond => {
                    return new priority.netitems.form.AlertCondition({
                        CurrentValue: cond.curvalue ? Number(cond.curvalue) : null,
                        ConditionOperator: cond.op ? Number(cond.op) : null,
                        ConditionField: cond.field ? Number(cond.field) : null,
                        ConditionValue: cond.value,
                        ConditionSecondValue: cond.secvalue
                    });
                }) || []
        });
    });
};

export const alertsToStorageSerializer = (session, data: object, fromId, toId) => {
    const storage: AlertOverviewType[] = [];
    Object.keys(data).forEach(key => {
        const conditions = Object.values(
            data[key].Conditions || {}
        ) as AlertOverviewType['Conditions'];
        const rule: AlertOverviewType = {
            action: data[key].action,
            alert: data[key].alert,
            condition: data[key].condition,
            details: data[key].details,
            description: data[key].description,
            isActive: data[key].isActive ? 'Y' : 'N',
            title: data[key].title,
            user: data[key].user,
            Conditions: conditions?.length > 0 ? conditions : undefined,
            conditionsOperator: data[key].conditionsOperator
        };
        storage.push(rule);
    });
    bpmRulesStorage.getStorage(session).setRules(fromId, toId, storage);
};
